import React, { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';
import { apiClient } from '../../utils/api';
import '../../styles/agent.css';
import '../../styles/agent-debug.css';
import ErrorMessage from '../common/ErrorMessage';
import AgentDebugPanel from './AgentDebugPanel';

const DeviceSelector = ({ devices, onSelect }) => (
    <div className="device-selector">
        <p className="device-selector-label">Which device would you like to diagnose?</p>
        <div className="device-selector-options">
            {devices.map((device, idx) => (
                <button
                    key={idx}
                    className="device-selector-btn"
                    onClick={() => onSelect(device)}
                >
                    <span className="device-selector-name">{device.name || device.mac}</span>
                    <span className="device-selector-mac">{device.mac}</span>
                </button>
            ))}
        </div>
    </div>
);

const tryParseDeviceSelection = (content) => {
    try {
        const match = content.match(/```json\s*([\s\S]*?)\s*```/) || content.match(/(\{[\s\S]*"type"\s*:\s*"device_selection"[\s\S]*\})/);
        if (match) {
            const parsed = JSON.parse(match[1] || match[0]);
            if (parsed.type === 'device_selection' && Array.isArray(parsed.devices)) {
                return parsed;
            }
        }
    } catch (e) { /* not a device selection */ }
    return null;
};

const AgentChat = () => {
    const [messages, setMessages] = useState([
        { role: 'agent', content: 'Hello! I am the Diagnostics AI Agent. How can I help you investigate the Wi-Fi network today?' }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [debugEnabled, setDebugEnabled] = useState(false);
    const [debugData, setDebugData] = useState(null);
    const [debugVisible, setDebugVisible] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading]);

    const sendMessage = async (userMessage) => {
        setError(null);
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setLoading(true);

        try {
            const chatHistory = messages
                .filter((m, idx) => (m.role === 'user' || m.role === 'agent') && idx > 0)
                .slice(-6)
                .map(m => ({
                    role: m.role,
                    content: m.content.length > 1500 ? m.content.slice(0, 1500) + '\n[truncated]' : m.content,
                }));

            const response = await apiClient.chatWithAgent(userMessage, {
                includeDebug: debugEnabled,
                history: chatHistory,
            });

            if (debugEnabled && response.debug) {
                setDebugData(response.debug);
            }

            const agentResponse = response.response || response.text || response;
            const content = typeof agentResponse === 'string' ? agentResponse : JSON.stringify(agentResponse);
            const deviceSelection = tryParseDeviceSelection(content);

            setMessages(prev => [...prev, {
                role: 'agent',
                content: deviceSelection ? (deviceSelection.message || 'I found multiple devices on your account:') : content,
                deviceSelection: deviceSelection
            }]);
        } catch (err) {
            console.error('Agent chat error:', err);
            setError(err.message || 'Failed to communicate with the Agent.');
            setMessages(prev => [...prev, { role: 'error', content: 'Agent failed to respond. Please try again.' }]);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;
        const userMessage = input.trim();
        setInput('');
        await sendMessage(userMessage);
    };

    const handleDeviceSelect = async (device) => {
        const message = `Diagnose device ${device.name || ''} with MAC ${device.mac}`;
        await sendMessage(message);
    };

    return (
        <div className="agent-dashboard">
            <header className="agent-header">
                <div className="agent-header-icon">🤖</div>
                <div className="agent-header-text">
                    <h2>Diagnostics AI</h2>
                    <p>Powered by Gemini 2.5 Pro</p>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                    <button
                        className={`debug-toggle-btn ${debugEnabled ? 'active' : ''}`}
                        onClick={() => setDebugEnabled(prev => !prev)}
                        title="Toggle debug mode"
                    >
                        {debugEnabled ? 'Debug ON' : 'Debug OFF'}
                    </button>
                    {debugData && (
                        <button
                            className="debug-toggle-btn active"
                            onClick={() => setDebugVisible(true)}
                            title="View last debug trace"
                        >
                            View Debug
                        </button>
                    )}
                </div>
            </header>

            <div className="agent-chat-container">
                <div className="agent-chat-window">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`chat-message-row chat-macro-${msg.role}`}>
                            {msg.role === 'agent' && <div className="chat-avatar">AI</div>}
                            <div className={`chat-bubble chat-bubble-${msg.role}`}>
                                <div className="chat-bubble-content">
                                    <Markdown>{msg.content}</Markdown>
                                    {msg.deviceSelection && (
                                        <DeviceSelector
                                            devices={msg.deviceSelection.devices}
                                            onSelect={handleDeviceSelect}
                                        />
                                    )}
                                </div>
                            </div>
                            {msg.role === 'user' && <div className="chat-avatar">U</div>}
                        </div>
                    ))}
                    
                    {loading && (
                        <div className="chat-message-row chat-macro-agent">
                            <div className="chat-avatar">AI</div>
                            <div className="chat-bubble chat-bubble-agent chat-bubble-loading">
                                <div className="typing-indicator">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
                
                {error && <div style={{ padding: '0 20px' }}><ErrorMessage title="Error" message={error} /></div>}

                <form className="agent-chat-input-area" onSubmit={handleSend}>
                    <input
                        type="text"
                        className="agent-chat-input"
                        placeholder="Ask about a target device MAC..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        disabled={loading}
                    />
                    <button type="submit" className="agent-chat-send-btn" disabled={loading || !input.trim()}>
                        {loading ? '...' : 'Send'}
                    </button>
                </form>
            </div>

            <AgentDebugPanel
                debugData={debugData}
                visible={debugVisible}
                onClose={() => setDebugVisible(false)}
            />
        </div>
    );
};

export default AgentChat;
